import { useAuth } from '../contexts/AuthContext';

/**
 * Single source of truth for the current user's tier/entitlement in the UI
 * (paywall bridge, BAB-71). Reads the flags exposed on /auth/me.
 *
 *   subscription_tier: 'free' | 'pro' | 'premium' | null  (null = grandfathered)
 *   is_pro:            effective access (grandfathered / paid / active trial)
 *   trial_ends_at:     ISO string when a Pro trial expires
 */
export type TierLabel = 'Free' | 'Pro' | 'Premium' | 'Pro trial';

export interface Entitlement {
  tier: string | null | undefined;
  isPro: boolean;
  isTrialing: boolean;
  trialDaysLeft: number | null;
  /** Short label for the badge. */
  label: TierLabel;
  /** Free (and not trialing) → should see upgrade affordances. */
  canUpgrade: boolean;
}

export function useEntitlement(): Entitlement {
  const { user } = useAuth();
  const tier = user?.subscription_tier;
  const isPro = !!user?.is_pro;

  const trialEnds = user?.trial_ends_at ? new Date(user.trial_ends_at) : null;
  const trialActive = !!trialEnds && trialEnds.getTime() > Date.now();
  // A trialing user sits on 'free' tier with a future trial_ends_at.
  const isTrialing = trialActive && tier !== 'pro' && tier !== 'premium';
  const trialDaysLeft = trialActive
    ? Math.max(0, Math.ceil((trialEnds!.getTime() - Date.now()) / 86_400_000))
    : null;

  let label: TierLabel = 'Free';
  if (tier === 'premium') label = 'Premium';
  else if (tier === 'pro') label = 'Pro';
  else if (isTrialing) label = 'Pro trial';
  else if (isPro) label = 'Pro'; // grandfathered (null tier) = full access
  else label = 'Free';

  return {
    tier,
    isPro,
    isTrialing,
    trialDaysLeft,
    label,
    canUpgrade: !isPro && !isTrialing,
  };
}
